/**
 * BaseTable — antd Table wrapper with URL-driven state: per-column
 * search/date filters and sort directions are read from and written to the
 * query string (filters[field], sorts[field]) so table views are
 * deep-linkable and survive reloads. Used by Table/Zones.jsx.
 */
import React, { useState, useEffect } from "react";
import { Table, Input, Button, DatePicker } from "antd";
import { SearchOutlined, FilterFilled, CloseOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  DATE_FORMAT_BACKEND,
  DATE_FORMAT_FRONTEND,
  PAGE_SIZE,
} from "../../constants";

function flatten(arr) {
  return arr.reduce(function (flat, toFlatten) {
    return flat.concat(
      Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten
    );
  }, []);
}

function getSortDirection(field, query) {
  const fieldName = Array.isArray(field) ? field.join("_") : field;
  const direction = query[`sorts[${fieldName}]`] + "end";
  if (direction === "ascend" || direction === "descend") {
    return direction;
  }
  return undefined;
}

function getFilteredValue(field, query) {
  const fieldName = Array.isArray(field) ? field.join("_") : field;
  const fieldValue = query[`filters[${fieldName}]`];
  return fieldValue ? [fieldValue] : null;
}

function getColumnSearchProps(type) {
  return {
    filterDropdown: (filterDropdownProps) => {
      const { setSelectedKeys, selectedKeys, confirm, clearFilters } =
        filterDropdownProps;
      return (
        <div style={{ padding: 8 }}>
          {type === "date" && (
            <DatePicker
              style={{ width: 188, marginBottom: 8, display: "block" }}
              format={DATE_FORMAT_FRONTEND}
              value={
                selectedKeys[0]
                  ? moment(selectedKeys[0], DATE_FORMAT_BACKEND)
                  : null
              }
              onChange={(e) =>
                setSelectedKeys(e ? [e.format(DATE_FORMAT_BACKEND)] : [])
              }
            />
          )}
          {type !== "date" && (
            <Input
              placeholder="Search"
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={confirm}
              style={{ width: 188, marginBottom: 8, display: "block" }}
            />
          )}
          <Button
            size="small"
            style={{ width: 90, marginRight: 8 }}
            onClick={clearFilters}
          >
            Reset
          </Button>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
            onClick={confirm}
          >
            Search
          </Button>
        </div>
      );
    },
    filterIcon: (filtered) =>
      type === "date" ? (
        <FilterFilled style={{ color: filtered ? "#050593" : undefined }} />
      ) : (
        <SearchOutlined style={{ color: filtered ? "#050593" : undefined }} />
      ),
  };
}

const BaseTable = ({
  bulkUpdate,
  pagination,
  query = {},
  columns = [],
  onChange2,
  modifying,
  defaultSelectedItems,
  setDefaultSelectedItems,
  getCheckboxProps,
  selectedItems,
  setSelectedItems,
  ...props
}) => {
  const [state, setState] = useState({ selection: {} });

  useEffect(() => {
    if (!modifying) {
      setState({ selection: {} });
    }
  }, [modifying]);

  useEffect(() => {
    if (defaultSelectedItems && props.dataSource) {
      setState({
        selection: {
          ...state.selection,
          [pagination.current]: !selectedItems
            ? defaultSelectedItems
            : selectedItems,
        },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelectedItems, props.dataSource]);

  function handleTableChange(pagination, filter, sorter) {
    const nextQuery = {
      currentPage: pagination.current,
      itemsPerPage: pagination.pageSize,
    };
    if (sorter.order && sorter.field) {
      const sortField = `sorts[${
        Array.isArray(sorter.field) ? sorter.field.join("_") : sorter.field
      }]`;
      const sortValue = sorter.order.replace("end", "");
      nextQuery[sortField] = sortValue;
    }
    for (const field of Object.keys(filter)) {
      if (filter[field]) {
        const filterField = `filters[${field.replace(".", "_")}]`;
        let filterValue = filter[field];
        if (Array.isArray(filterValue)) {
          filterValue = filterValue.join(",").trim();
        } else {
          filterValue =
            typeof filterValue === "string" ? filterValue.trim() : filterValue;
        }
        nextQuery[filterField] = filterValue;
      }
    }
    onChange2 && onChange2(nextQuery);
  }

  const COLUMNS = columns.map((colProps) => {
    const { searchable, ...rest } = colProps;
    return {
      ...rest,
      ...(rest.sorter
        ? { sortOrder: getSortDirection(rest.dataIndex, query) }
        : {}),
      ...(rest.filters || searchable
        ? { filteredValue: getFilteredValue(rest.dataIndex, query) }
        : {}),
      ...(searchable ? getColumnSearchProps(searchable) : {}),
    };
  });

  if (bulkUpdate) {
    COLUMNS.splice(0, 0, {
      key: "-",
      fixed: "left",
      width: 1,
    });
  }

  const selectionPopover =
    bulkUpdate && bulkUpdate(flatten(Object.values(state.selection)));

  const rowSelection = bulkUpdate && {
    selectedRowKeys: flatten(Object.values(state.selection)).map(
      (row) => row._id
    ),
    onChange: (_, selectedRows) => {
      setSelectedItems && setSelectedItems(selectedRows);
      setState({
        selection: {
          ...state.selection,
          [pagination.current]: selectedRows.filter((s) => s),
        },
      });
    },
    columnWidth: 40,
    getCheckboxProps: getCheckboxProps,
  };

  const paginationProps = {
    showSizeChanger: true,
    current: Number(query.currentPage) || 1,
    pageSize: Number(query.itemsPerPage) || PAGE_SIZE,
    locale: { items_per_page: 'Sida' },
    ...pagination,
  };

  const handleResetSelection = () => {
    setDefaultSelectedItems && setDefaultSelectedItems([]);
    setState({ selection: {} });
  };

  return (
    <div className="table-base-wrapper">
      {selectionPopover && (
        <div className="selection-popover">
          <span className="text-selected">
            {selectionPopover.title}
            {selectionPopover.title && (
              <Button
                size="small"
                type="link"
                icon={<CloseOutlined />}
                className="close"
                onClick={handleResetSelection}
                disabled={rowSelection.selectedRowKeys.length <= 0}
              />
            )}
          </span>
          {selectionPopover.actions}
        </div>
      )}
      <Table
        rowKey="id"
        rowSelection={rowSelection}
        showSorterTooltip={false}
        pagination={pagination && paginationProps}
        columns={COLUMNS}
        scroll={{ x: 2200 }}
        onChange={handleTableChange}
        {...props}
      />
    </div>
  );
};

export default BaseTable;
